import { autoinject } from 'aurelia-framework';
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { IBaseAuthProvider } from './base-auth-provider';
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { UrlHelper } from '../../helpers/url-helper';
import { WebAuth } from "auth0-js";
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';

@autoinject
export class Auth0Provider implements IBaseAuthProvider {

    public redirectUri;
    public clientId;
    public auth0;

    constructor(private cacheClient: CacheClientService, private requestClient: RequestClientService, private configService: ConfigService) {
        this.redirectUri = this.configService.config.common.auth0Settings.redirectUri;
        this.clientId = this.configService.config.common.auth0Settings.clientId;
        this.auth0 = new WebAuth({
            domain: this.configService.config.common.auth0Settings.domain,
            clientID: this.clientId,
            redirectUri: this.redirectUri,
            audience: this.configService.config.common.auth0Settings.audience,
            responseType: 'token id_token',
            scope: 'openid email profile app_metadata user_metadata'
        });
    }

    loadProvider(): Promise<void> {
        return new Promise<void>((resolve) => {
            resolve();
        });
    }

    redirectToLogin(prompt: string) {
        const referrer = UrlHelper.getParameterValueByKey('referrer', location.href);
        const campaign = UrlHelper.getParameterValueByKey('utm_campaign', location.href);
        const encodedUrl = encodeURIComponent((referrer ?? location.origin));
        let redirectUri = this.redirectUri.split("?")[0];
        if (campaign) {
            redirectUri += `?utm_campaign=${campaign}&referrer=${encodedUrl}`;
        } else {
            redirectUri += `?referrer=${encodedUrl}`;
        }
        this.auth0.authorize({ prompt: prompt, redirectUri: redirectUri});
    }

    getAuthToken(callback: (response: { accessToken: string, userId: string, subProviderName: string, metadata: string, doNotSaveUserLogin: boolean }) => void) {

        this.auth0.parseHash((err, authResult) => {
            if (authResult && authResult.accessToken && authResult.idToken) {
                const tokenPayload = authResult.idTokenPayload;
                const uniqueIdentifier = tokenPayload.sub.split("|");
                const providerKey = uniqueIdentifier[1];
                const providerName = uniqueIdentifier[0];
                const subProviderName = providerName.split("-")[0];
                const userMetadata = authResult.idTokenPayload["https://" +  this.configService.config.common.auth0Settings.domain.replace(/[.]/g, ':') + "/user_metadata"];
                let doNotSaveUserLogin = false;

                if (subProviderName == 'samlp') {
                    doNotSaveUserLogin = true;
                }

                this.cacheClient.put('auth0_access_token', authResult.accessToken, CacheLocation.LocalStorage).then(() => {
                    callback({
                        accessToken: authResult.accessToken,
                        userId: providerKey,
                        subProviderName: subProviderName,
                        metadata: JSON.stringify(userMetadata),
                        doNotSaveUserLogin: doNotSaveUserLogin
                    });
                });
            } else if (err) {
                this.redirectToLogin("login");
            }
        });
    }

    fetchUserDetails(): Promise<{ firstName: string, lastName: string, email: string, picture: string }> {
        return new Promise<{ firstName: string, lastName: string, email: string, picture: string }>((resolve) => {
            this.cacheClient.get<string>('auth0_access_token', CacheLocation.LocalStorage).then(token => {
                this.auth0.client.userInfo(token, function (err, user) {
                    resolve({ firstName: user.nickname, lastName: null, email: user.email, picture: user.picture });
                });
            });
        });
    }

    changePassword(): Promise<void> {
        return this.requestClient.post("auth0/changeAuth0Password", {});
    }

    logout() {
        return new Promise<void>((resolve, reject) => {
            const loginHref = (this.configService.config.common.auth0Settings.loginPath || '/login');
            const redirectUrl = `${location.origin}${loginHref.startsWith("/")? "": "/"}${loginHref}`;
            const logoutOptions = { clientID: this.clientId, returnTo: redirectUrl, federated: true};
            this.cacheClient.remove('auth0_access_token', CacheLocation.LocalStorage).then(() => {
                this.auth0.logout(logoutOptions);
                // resolve(); commented out as it was not allowing logout to happen properly, this.auth0.logout redirects when done and its return type is void so cannot be awaited
            })
            .catch((e) =>{
                this.auth0.logout(logoutOptions);
                reject(e);
            });
        })
    }
}
